/**
 * La puerta de entrada: un buscador y nada más.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * POR QUÉ SOLO EL BUSCADOR.
 *
 * Quien llega acá llega con un nombre en la cabeza —"guantes anticorte",
 * "rodamiento 6205", un número de parte— y lo que necesita es un campo donde
 * escribirlo. La portada anterior abría con la grilla del catálogo, que es una
 * buena vitrina para pasear y una mala respuesta para quien ya sabe qué busca:
 * lo obligaba a encontrar el buscador entre los productos.
 *
 * NO HAY ILUSTRACIÓN DE FONDO, y es una decisión, no una omisión. Se probó con
 * un diagrama del recorrido detrás del campo y se veía mal: dos planos
 * compitiendo por la misma atención, con el dibujo asomando por los bordes en
 * cada tamaño de pantalla distinto. Una portada de un solo foco no admite un
 * segundo elemento visual; lo que la vuelve seria es el aire alrededor del
 * campo, no lo que se le ponga detrás.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * SE CONSTRUYE CON EL SISTEMA, no con estilos propios: radios de la escala
 * —3 a 6 px, nunca una píldora—, foco `marca-500` con anillo de 1 px como
 * `.campo`, y CERO SOMBRAS, que es una regla del manual y no una preferencia:
 * no hay un solo `shadow-` en el resto de la aplicación. Un buscador con
 * sombra suave y bordes redondos es de otro sistema visual, y se nota.
 *
 * LAS SUGERENCIAS SON DEL SERVIDOR. Salen de `GET /api/catalogo`, el mismo
 * endpoint que usa la vitrina, con su índice FULLTEXT y sus coincidencias por
 * código y número de parte. No hay una segunda implementación de «qué calza con
 * este texto» que pueda dar un resultado distinto al de la búsqueda de verdad.
 *
 * Y NO SE MUESTRA NADA QUE NO CORRESPONDA: es la proyección pública del
 * catálogo, así que sin sesión no viaja el stock exacto ni el proveedor, y el
 * costo no viaja nunca. Acá se pinta el nombre, la marca y el precio de lista,
 * que es lo que ya está publicado.
 *
 * Elegir una sugerencia lleva a la ficha del artículo. Pulsar Enter sin elegir
 * ninguna lleva al catálogo con la búsqueda aplicada por la URL — que es lo que
 * hace falta para que este buscador y el de la vitrina sean el mismo, y no dos
 * cajas que se contradicen.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { ItemCatalogo } from '@compranorte/contracts'
import { MINIMO_SUGERENCIA, useSugerencias } from '../api/hooks'
import { IconoBuscar, IconoFlecha } from '../componentes/icons'
import { Money } from '../componentes/ui'

/** Espera antes de preguntar. Una tecla no es una consulta. */
const ESPERA = 220

export function Portada() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [qLento, setQLento] = useState('')
  /** Índice resaltado con el teclado. -1 es «ninguno, va la búsqueda entera». */
  const [activo, setActivo] = useState(-1)
  const [abierto, setAbierto] = useState(false)
  const caja = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = setTimeout(() => setQLento(q.trim()), ESPERA)
    return () => clearTimeout(t)
  }, [q])

  const consulta = useSugerencias(qLento)
  const sugerencias: ItemCatalogo[] = consulta.data?.datos ?? []

  const texto = q.trim()
  const desplegado = abierto && texto.length >= MINIMO_SUGERENCIA

  /*
   * Un clic fuera cierra el desplegable. Sin esto queda flotando sobre la
   * página después de que la persona ya se fue a otra cosa, tapando lo de abajo
   * con resultados de una búsqueda que abandonó.
   */
  useEffect(() => {
    if (!desplegado) return
    function alClic(ev: MouseEvent) {
      if (caja.current && !caja.current.contains(ev.target as Node)) setAbierto(false)
    }
    document.addEventListener('mousedown', alClic)
    return () => document.removeEventListener('mousedown', alClic)
  }, [desplegado])

  /*
   * El resaltado vuelve al principio en cuanto cambia la lista: mantenerlo en
   * la posición 5 de una lista que ahora tiene dos filas apuntaría a otra cosa,
   * y con Enter se abriría un producto que nadie eligió.
   */
  useEffect(() => setActivo(-1), [qLento])

  function irAlCatalogo() {
    if (!texto) return navigate('/catalogo')
    navigate(`/catalogo?q=${encodeURIComponent(texto)}`)
  }

  function elegir(indice: number) {
    const item = sugerencias[indice]
    if (item) navigate(`/producto/${encodeURIComponent(item.id)}`)
    else irAlCatalogo()
  }

  function alTeclado(ev: React.KeyboardEvent<HTMLInputElement>) {
    if (ev.key === 'Escape') {
      setAbierto(false)
      return
    }
    if (ev.key === 'Enter') {
      ev.preventDefault()
      elegir(activo)
      return
    }
    if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return
    ev.preventDefault()
    if (!desplegado || sugerencias.length === 0) return

    /*
     * Se recorre de -1 a n-1: la posición -1 es «buscar el texto completo», que
     * es una opción legítima y tiene que poder recuperarse con la flecha. Da la
     * vuelta por los dos extremos, que es lo que hace cualquier desplegable.
     */
    const total = sugerencias.length
    const paso = ev.key === 'ArrowDown' ? 1 : -1
    setActivo((i) => {
      const siguiente = i + paso
      if (siguiente < -1) return total - 1
      if (siguiente >= total) return -1
      return siguiente
    })
  }

  return (
    /*
     * El bloque se apoya arriba del centro óptico, no en el centro exacto: el
     * ojo lee como "centrado" algo que está un poco más alto, y centrado de
     * verdad se ve caído. `vh` y no una altura calculada a mano, que se
     * desajusta cada vez que la barra de arriba cambia de alto.
     */
    <div className="flex justify-center px-2 pt-[11vh] pb-[18vh]">
      <div className="w-full max-w-[620px]">
        <div className="text-center">
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-acero-500">
            Distribución digital de insumos de faena
          </span>
          {/*
            El titular es la promesa completa en una línea, con el número al
            frente. `text-balance` reparte las dos líneas en pantallas angostas
            en vez de dejar una palabra suelta abajo.
          */}
          <h1 className="mt-2.5 text-balance text-[30px] sm:text-[38px] font-semibold leading-[1.1] tracking-[-0.02em] text-acero-950">
            Un proveedor. Una orden. Una factura.
          </h1>
          <p className="mx-auto mt-3 max-w-[430px] text-[13.5px] leading-relaxed text-acero-600">
            Busque el insumo que necesita y véalo con precio, peso y plazo a la vista.
          </p>
        </div>

        <div ref={caja} className="relative mt-7">
          <label className="block">
            <span className="sr-only">Buscar en el catálogo</span>
            <IconoBuscar
              size={18}
              className="pointer-events-none absolute left-3.5 top-6 -translate-y-1/2 text-acero-400"
            />
            <input
              type="search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value)
                setAbierto(true)
              }}
              onFocus={() => setAbierto(true)}
              onKeyDown={alTeclado}
              placeholder="Guantes anticorte, rodamiento 6205, un código…"
              aria-label="Buscar en el catálogo"
              aria-expanded={desplegado}
              aria-autocomplete="list"
              aria-controls="sugerencias-portada"
              role="combobox"
              /*
               * Las esquinas de abajo se cuadran cuando la lista está abierta:
               * el campo y el desplegable pasan a leerse como una sola pieza en
               * vez de dos cajas apiladas con una junta a la vista.
               */
              className={`h-12 w-full border border-acero-300 bg-white pl-11 pr-[104px]
                text-[15px] text-acero-900 placeholder:text-acero-400 outline-none
                transition-colors hover:border-acero-400
                focus:border-marca-500 focus:ring-1 focus:ring-marca-500/30
                ${desplegado ? 'rounded-t-md rounded-b-none' : 'rounded-md'}`}
            />
          </label>

          {/*
            El botón vive DENTRO del campo, a la derecha. Es la acción de la
            pantalla y por eso lleva amarillo señal, que en este sistema se usa
            solo para eso. Sin él, el único camino visible sería Enter — que
            quien usa el mouse no tiene por qué adivinar.
          */}
          <button
            type="button"
            onMouseDown={(ev) => {
              ev.preventDefault()
              elegir(activo)
            }}
            className="btn btn-accion absolute right-1.5 top-1.5 h-9 px-4"
          >
            Buscar
          </button>

          {desplegado && (
            <ul
              id="sugerencias-portada"
              role="listbox"
              className="absolute z-30 w-full overflow-hidden rounded-b-md border border-t-0
                border-acero-300 bg-white"
            >
              {sugerencias.map((p, i) => (
                <li key={p.id} role="option" aria-selected={activo === i}>
                  <button
                    type="button"
                    /* `onMouseDown` y no `onClick`: el clic normal llega después
                       del `blur`, y para entonces el desplegable ya se cerró y
                       el botón que se estaba pulsando no existe. */
                    onMouseDown={(ev) => {
                      ev.preventDefault()
                      elegir(i)
                    }}
                    onMouseEnter={() => setActivo(i)}
                    className={`flex w-full items-center gap-3 border-t border-acero-200 px-4 py-2.5
                      text-left first:border-t-0 ${activo === i ? 'bg-acero-50' : 'bg-white'}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] text-acero-900">{p.nombre}</span>
                      <span className="block text-[11.5px] text-acero-500">
                        {p.marca} · <span className="num">{p.codigo}</span>
                        {!p.hayStock && ' · agotado'}
                      </span>
                    </span>
                    {/* El precio de lista ya es público: mostrarlo acá evita
                        abrir la ficha solo para descartar por precio. */}
                    <span className="shrink-0 text-[13.5px] font-semibold text-acero-900">
                      <Money v={p.precioVentaNeto} />
                    </span>
                  </button>
                </li>
              ))}

              {/*
                La última fila SIEMPRE está, con o sin coincidencias: es la
                salida hacia la búsqueda completa, con filtros, orden y
                paginación. Sin ella, cero sugerencias sería el final del camino
                — y ahí es justamente donde la vitrina ofrece registrar lo que
                nos piden y no tenemos.
              */}
              <li role="option" aria-selected={activo === -1}>
                <button
                  type="button"
                  onMouseDown={(ev) => {
                    ev.preventDefault()
                    irAlCatalogo()
                  }}
                  onMouseEnter={() => setActivo(-1)}
                  className={`flex w-full items-center gap-2 border-t border-acero-200 px-4 py-2.5
                    text-left text-[12.5px] ${activo === -1 ? 'bg-acero-50' : 'bg-acero-50/40'}`}
                >
                  <IconoBuscar size={14} className="shrink-0 text-acero-500" />
                  <span className="min-w-0 flex-1 truncate text-acero-700">
                    Buscar <strong className="text-acero-900">{texto}</strong> en todo el catálogo
                  </span>
                  <IconoFlecha size={14} className="shrink-0 text-acero-400" />
                </button>
              </li>

              {consulta.isPending && sugerencias.length === 0 && (
                <li className="border-t border-acero-200 px-4 py-2.5 text-[12px] text-acero-500">
                  Buscando…
                </li>
              )}
            </ul>
          )}
        </div>

        {/*
          Una sola salida, y discreta: quien no sabe qué escribir necesita poder
          pasear la vitrina. El resto de la navegación —entrar, crear cuenta—
          ya está en la barra de arriba, y repetirla acá volvería a llenar de
          contenido la pantalla que se vació a propósito.
        */}
        <p className="mt-5 text-center text-[12.5px] text-acero-500">
          o{' '}
          <Link to="/catalogo" className="text-marca-600 hover:underline">
            recorra todo el catálogo
          </Link>
        </p>
      </div>
    </div>
  )
}
